import React from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { typographyScale } from "../../../../../styles/theme";
import { CATEGORIES } from "./wbsConfig";
import { budgetCost, catKeyOf, estCost } from "./buildDashboardLines";
import { money, qty, rate } from "./format";

// The same tokens the PV Shell / Nozzle tables paint with (styles/theme.js).
const BORDER = "1px solid var(--pv-table-border, rgba(170, 180, 198, 0.6))";
const HEADER_BG = "var(--pv-table-header-bg, hsla(250, 86%, 97%, 1))";
const HEADER_TEXT = "var(--pv-table-header-text, inherit)";

const Dash = () => (
  <Typography component="span" variant="inherit" color="text.disabled">
    —
  </Typography>
);

const amount = (value) => (value == null ? <Dash /> : money(value));
// Category rows stay quiet when a measure has nothing; the total row says "—".
const categoryAmount = (t, key) => (t.has[key] ? money(t[key]) : "");
const totalAmount = (t, key) => (t.has[key] ? money(t[key]) : <Dash />);

const GROUP_LABEL = {
  est: "Estimate",
  bud: "Budget",
  act: "Actual",
  inv: "Invoiced",
  pd: "Paid",
};

// One spec per column, so header, line, category and total cells can't drift
// apart. Actual and Paid only exist as costs: POs carry no quantity in the
// estimate's unit (Kg / Manhour), so there is nothing honest to show for one.
const MEASURE_COLUMNS = {
  est: [
    { id: "eq", head: "Est Unit", line: (l) => qty(l.eq) },
    { id: "er", head: "Est Unit Rate", line: (l) => rate(l.er) },
    {
      id: "ec",
      head: "Est Cost",
      strong: true,
      line: (l) => amount(estCost(l)),
      category: (t) => categoryAmount(t, "est"),
      total: (t) => totalAmount(t, "est"),
    },
  ],
  bud: [
    { id: "bq", head: "Bud Unit", line: (l) => qty(l.bq) },
    { id: "br", head: "Bud Rate", line: (l) => rate(l.br) },
    {
      id: "bc",
      head: "Bud Cost",
      strong: true,
      line: (l) => amount(budgetCost(l)),
      category: (t) => categoryAmount(t, "bud"),
      total: (t) => totalAmount(t, "bud"),
    },
  ],
  act: [
    {
      id: "ac",
      head: "Actual Cost",
      strong: true,
      line: (l) => amount(l.ac),
      category: (t) => categoryAmount(t, "act"),
      total: (t) => totalAmount(t, "act"),
    },
  ],
  inv: [
    {
      id: "ip",
      head: "Inv %",
      line: (l) => (l.ip != null ? `${l.ip}%` : ""),
      category: (t, invoice) =>
        invoice?.progress != null ? `${invoice.progress}%` : "",
    },
    {
      id: "ia",
      head: "Invoiced $",
      strong: true,
      line: (l) => amount(l.ia),
      category: (t) => categoryAmount(t, "inv"),
      total: (t) => totalAmount(t, "inv"),
    },
  ],
  pd: [
    {
      id: "pc",
      head: "Paid Cost",
      strong: true,
      line: (l) => amount(l.pc),
      category: (t) => categoryAmount(t, "pd"),
      total: (t) => totalAmount(t, "pd"),
    },
  ],
};

const monoSx = { fontFamily: (theme) => theme.typography.caption.fontFamily };
const numberSx = { textAlign: "right", fontVariantNumeric: "tabular-nums" };

/**
 * @param show     { est, bud, act, inv, pd, details } - which column groups to draw
 * @param totals   aggregate() result (overall + byCat)
 * @param invoices extractInvoices() result, keyed by discipline
 * @param toolbar  optional node drawn as the table's top bar, directly above the header
 */
export default function DashboardTable({
  lines,
  totals,
  invoices,
  show,
  tag,
  toolbar,
}) {
  const measureKeys = Object.keys(MEASURE_COLUMNS).filter((key) => show[key]);
  const measureColumns = measureKeys.flatMap((key) => MEASURE_COLUMNS[key]);
  // WBS Code, Task Description, [Entity, UID Register], Unit
  const identityCount = show.details ? 5 : 3;

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      {toolbar}
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 640,
            "& .MuiTableCell-root": {
              borderRight: BORDER,
              borderBottom: BORDER,
              px: 1,
              py: 0.75,
              fontSize: typographyScale.fontSize.sm,
              whiteSpace: "nowrap",
            },
            "& .MuiTableCell-root:last-of-type": { borderRight: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={identityCount}
                align="center"
                sx={{ bgcolor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700 }}
              >
                WBS
              </TableCell>
              {measureKeys.map((key) => (
                <TableCell
                  key={key}
                  colSpan={MEASURE_COLUMNS[key].length}
                  align="center"
                  sx={{ bgcolor: HEADER_BG, color: HEADER_TEXT, fontWeight: 700 }}
                >
                  {GROUP_LABEL[key]}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {[
                "WBS Code",
                "Task Description",
                ...(show.details ? ["Entity", "UID Register"] : []),
                "Unit",
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    bgcolor: HEADER_BG,
                    color: HEADER_TEXT,
                    fontSize: typographyScale.fontSize.xs,
                  }}
                >
                  {head}
                </TableCell>
              ))}
              {measureColumns.map((col) => (
                <TableCell
                  key={col.id}
                  align="right"
                  sx={{
                    bgcolor: HEADER_BG,
                    color: HEADER_TEXT,
                    fontSize: typographyScale.fontSize.xs,
                  }}
                >
                  {col.head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {CATEGORIES.map((cat) => {
              const catLines = lines.filter((line) => catKeyOf(line) === cat.k);
              if (catLines.length === 0) return null;
              const invoice = invoices[cat.discipline];
              const catTotals = totals.byCat[cat.k];

              return (
                <React.Fragment key={cat.k}>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.08),
                        fontWeight: 700,
                      },
                    }}
                  >
                    <TableCell sx={monoSx}>{cat.code}</TableCell>
                    <TableCell>
                      {cat.name}{" "}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={cat.sys}
                        sx={{ height: 18, ml: 0.5, fontSize: "0.625rem" }}
                      />
                    </TableCell>
                    {show.details && <TableCell colSpan={2} />}
                    <TableCell>{cat.unit}</TableCell>
                    {measureColumns.map((col) => (
                      <TableCell key={col.id} sx={numberSx}>
                        {col.category ? col.category(catTotals, invoice) : ""}
                      </TableCell>
                    ))}
                  </TableRow>

                  {catLines.map((line) => (
                    <TableRow key={line.code} hover>
                      <TableCell sx={{ ...monoSx, color: "primary.main" }}>
                        {line.displayCode}
                      </TableCell>
                      <TableCell>{line.desc}</TableCell>
                      {show.details && (
                        <>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={line.entity}
                              sx={{ height: 18, fontSize: "0.625rem" }}
                            />
                          </TableCell>
                          <TableCell
                            title={line.uid || undefined}
                            sx={{
                              ...monoSx,
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {line.uid || <Dash />}
                          </TableCell>
                        </>
                      )}
                      <TableCell>{line.unit}</TableCell>
                      {measureColumns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{
                            ...numberSx,
                            fontWeight: col.strong ? 600 : 400,
                          }}
                        >
                          {col.line(line)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}

            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  bgcolor: "action.hover",
                  fontWeight: 700,
                  borderTop: (theme) => `2px solid ${theme.palette.primary.main}`,
                },
              }}
            >
              <TableCell colSpan={identityCount}>
                Total{tag ? ` · ${tag}` : ""}
              </TableCell>
              {measureColumns.map((col) => (
                <TableCell key={col.id} sx={numberSx}>
                  {col.total ? col.total(totals) : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
